import { forwardRef, useState } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import { MapMarkerIcon } from '@schuettflix/planum-icons-react';
import { useWizardPanelContext } from '@/shared/components/Wizard.tsx';
import { PanelInputButton } from '@/shared/inputs/PanelInputButton.tsx';
import { LocationConfigurationPanel } from '@/modules/location/LocationConfigurationPanel.tsx';
import { LocationConfiguration } from '@/modules/location/domain/LocationConfigurationSchema.ts';
import { formatAddress } from '@/modules/location/domain/AddressSchema.ts';
import { LocationType } from '@/modules/location/domain/LocationSchema.ts';
import { LocationSource } from '@/clients/location/useLocations.ts';

type LocationFieldValues = {
    [key: string]: LocationConfiguration;
};

export type LocationInputProps = Pick<UseControllerProps<LocationFieldValues, string>, 'name' | 'defaultValue'> & {
    defaultOrganizationId?: number;
    className?: string;
    locationSources: LocationSource[];
    label: string;
    locationType: LocationType;
    onValueChange?: (location: LocationConfiguration | null) => void;
};

export const LocationInput = forwardRef<HTMLInputElement, LocationInputProps>(
    ({ name, locationType, label, locationSources, defaultOrganizationId, className, onValueChange }, _ref) => {
        const { control } = useFormContext<LocationFieldValues>();
        const { hasOpenedPanel } = useWizardPanelContext();
        const [isPanelOpen, setIsPanelOpen] = useState(false);

        const canOpenPanel = locationSources.length > 0;
        const showPanel = isPanelOpen && canOpenPanel;

        return (
            <Controller
                name={name}
                control={control}
                disabled={hasOpenedPanel || !locationSources.length}
                render={({ field: { onChange, value, disabled }, fieldState }) => (
                    <>
                        <PanelInputButton
                            leadingSlot={<MapMarkerIcon monocrome />}
                            isHighlighted={showPanel}
                            label={label}
                            value={value ? formatAddress(value.location.geoAddress.address) : undefined}
                            onClick={() => setIsPanelOpen(true)}
                            disabled={disabled}
                            className={className}
                            isInvalid={fieldState.invalid}
                            errorMessage={fieldState.error?.message}
                            data-test={`location-input-button-${locationType.toLowerCase()}`}
                        />
                        {showPanel && (
                            <LocationConfigurationPanel
                                locationType={locationType}
                                value={value ?? null}
                                onClose={() => setIsPanelOpen(false)}
                                onSubmit={locationFormData => {
                                    onChange(locationFormData);
                                    onValueChange?.(locationFormData);
                                    setIsPanelOpen(false);
                                }}
                                source={locationSources}
                                defaultOrganizationId={defaultOrganizationId}
                            />
                        )}
                    </>
                )}
            />
        );
    }
);
